/**
 * Orchestration Action Applier Service
 *
 * Applies AI-generated orchestration actions by calling the appropriate backend endpoints.
 * Uses a phased approach:
 *   Phase 0: Apply a `create_orchestration` action (when present) and capture the new
 *            orchestration_id, threading it into all subsequent calls.
 *   Phase 1: Apply add_orchestration_step actions sequentially (maintaining order)
 *   Phase 2: Apply modification actions grouped by step_order (scripts, headers, body,
 *            auth, query params, environment, dataset)
 *   Phase 3: Apply reorder_steps actions
 *   Phase 4: Apply remove_step actions LAST (so step_order references stay valid)
 *
 * Phase 0 is fatal: if the create call fails, the rest of the sequence is aborted.
 * Phases 1–4 are error resilient — if one action fails, remaining actions
 * continue to be applied and failures accumulate into `failed` / `warnings`.
 *
 * Requirements: 4.3, 4.4, 5.6, 7.1, 7.2, 7.3, 7.5, 8.1, 8.6, 9.2, 9.3, 9.4, 9.5, 11.2
 */

import api from '../../../shared/utils/api';
import { toModeProperties } from '../../../shared/utils/aiBodyRows';
import { authHeader } from '../../../shared/auth/session';

/**
 * API endpoint paths for orchestration step operations.
 */
const ENDPOINTS = {
  CREATE_ORCHESTRATION: '/api/orchestration/createOrchestration',
  ADD_API_TO_ORCHESTRATION: '/api/orchestration/addAPIToOrchestrationV2',
  UPDATE_STEP_SCRIPT: '/api/orchestration/updateOrchestrationStepScript',
  UPDATE_STEP_HEADERS: '/api/orchestration/updateOrchestrationStepHeaders',
  UPDATE_STEP_BODY: '/api/orchestration/updateOrchestrationStepRequestBody',
  UPDATE_STEP_AUTHORIZATION: '/api/orchestration/updateOrchestrationStepAuthorization',
  UPDATE_STEP_QUERY_PARAMS: '/api/orchestration/updateOrchestrationStepQueryParams',
  DELETE_STEP_BY_ORDER: '/api/orchestration/deleteOrchestrationStepByOrder',
  REORDER_STEPS: '/api/orchestration/reorderOrchestrationSteps',
  UPDATE_DEFAULT_ENVIRONMENT: '/api/orchestration/updateOrchestrationDefaultEnvironment',
  UPDATE_DEFAULT_DATASET: '/api/orchestration/updateOrchestrationDefaultDataset',
};

/**
 * Apply AI-generated orchestration actions by calling backend endpoints.
 *
 * Actions are applied in phases:
 * 0. (Optional) `create_orchestration` action — provisions a brand-new orchestration
 *    on the backend. The returned `orchestration_id` becomes the target id for the
 *    rest of the sequence. If this fails, the whole sequence is aborted.
 * 1. All `add_orchestration_step` actions (sequential, in step_order)
 * 2. Modification actions grouped by step_order
 * 3. `reorder_steps` actions
 * 4. `remove_step` actions (applied last)
 *
 * @param {Array} actions - Validated orchestration actions from AI
 * @param {number|null} orchestrationId - Externally supplied orchestration id (used
 *   when no `create_orchestration` action is present in the sequence)
 * @returns {Promise<{applied: number, failed: Array, warnings: Array, aborted?: boolean, createdOrchestrationId?: number}>}
 */
export async function applyOrchestrationActions(actions, orchestrationId) {
  const results = { applied: 0, failed: [], warnings: [] };

  if (!Array.isArray(actions) || actions.length === 0) {
    results.warnings.push('No actions to apply');
    return results;
  }

  // Phase 0: Resolve target orchestration_id — provision a new orchestration when the
  // sequence carries a `create_orchestration` action; otherwise fall back to the
  // externally supplied orchestrationId. Phase 0 is fatal: a failure short-circuits
  // the entire sequence (Req 7.3).
  const createAction = actions.find((a) => a.type === 'create_orchestration');
  let targetOrchestrationId = orchestrationId;

  if (createAction) {
    // [AI-ORCH-DEBUG] Phase 0: about to create orchestration
    try {
      const created = await applyCreateOrchestration(createAction);
      targetOrchestrationId = created.orchestration_id;
      results.createdOrchestrationId = targetOrchestrationId;
    } catch (err) {
      console.error('[AI-ORCH-DEBUG] applier.phase0.failed', {
        error: err.message,
        stack: err.stack?.split('\n').slice(0, 3),
      });
      return {
        applied: 0,
        failed: [
          {
            action: createAction,
            error: err.message || 'Failed to create orchestration',
          },
        ],
        warnings: [],
        aborted: true,
      };
    }
  }

  if (!targetOrchestrationId) {
    results.warnings.push('Missing orchestration ID');
    return results;
  }

  // Phase 1: Add steps (must be sequential to maintain order)
  const addActions = actions
    .filter((a) => a.type === 'add_orchestration_step')
    .sort((a, b) => a.value.step_order - b.value.step_order);

  // [AI-ORCH-DEBUG] Phase 1 summary

  for (const action of addActions) {
    try {
      // Mirror the manual "Add Step" flow: seed the new orchestration step with
      // the underlying API request's real method/url/headers/query/body so the
      // step is not created blank. We fetch the API request detail by id and map
      // it into the add payload. If the fetch fails we fall back to a minimal
      // payload so the add still succeeds (just without seeded config).
      const seeded = await buildAddStepPayloadFromApiRequest(
        action.value.request_id
      );

      await api.post(ENDPOINTS.ADD_API_TO_ORCHESTRATION, {
        payload: {
          method: seeded.method,
          url: seeded.url,
          authorization: seeded.authorization,
          request_header: seeded.request_header,
          request_query_parameters: seeded.request_query_parameters,
          request_body: seeded.request_body,
          metadata: {
            orchestration_id: targetOrchestrationId,
            request_id: action.value.request_id,
            request_order: action.value.step_order,
            environment_id: null,
            dataset_id: null,
            user_id: null,
            concurrency: 1,
            iterations: 1,
            ramp_up_period: null,
            test_duration: null,
          },
        },
      });

      // addAPIToOrchestrationV2 does not persist scripts, so copy the source
      // API's pre/post scripts into the new step via the dedicated endpoint.
      // Best-effort: a script-copy failure must not undo the successful add.
      if (seeded.scripts && seeded.scripts.length > 0) {
        try {
          await api.post(ENDPOINTS.UPDATE_STEP_SCRIPT, {
            orchestration_id: targetOrchestrationId,
            step_order: action.value.step_order,
            scripts: seeded.scripts,
          });
        } catch (scriptErr) {
          console.warn('[AI-ORCH-DEBUG] applier.phase1.scriptCopyFailed', {
            requestId: action.value.request_id,
            stepOrder: action.value.step_order,
            error: scriptErr.message,
          });
        }
      }
      results.applied++;
    } catch (err) {
      results.failed.push({
        action,
        error: err.message || 'Failed to add step',
      });
    }
  }

  // Phase 2: Apply modifications grouped by step_order.
  // Excludes add_orchestration_step (Phase 1), create_orchestration (Phase 0),
  // reorder_steps (Phase 3) and remove_step (Phase 4) — those are applied in
  // their own phases so step_order references stay valid during the batch.
  const modActions = actions.filter(
    (a) =>
      a.type !== 'add_orchestration_step' &&
      a.type !== 'reorder_steps' &&
      a.type !== 'remove_step' &&
      a.type !== 'create_orchestration'
  );
  const byStep = groupByStepOrder(modActions);

  // [AI-ORCH-DEBUG] Phase 2 summary

  for (const [, stepActions] of Object.entries(byStep)) {
    for (const action of stepActions) {
      try {
        await applyModificationAction(action, targetOrchestrationId);
        results.applied++;
      } catch (err) {
        results.failed.push({
          action,
          error: err.message || 'Failed to apply modification',
        });
      }
    }
  }

  // Phase 3: Reorder steps. Applied AFTER all step modifications/adds so that
  // step_order references used by Phase 2 remain valid. Error resilient: a
  // failure is accumulated, not fatal.
  const reorderActions = actions.filter((a) => a.type === 'reorder_steps');

  // [AI-ORCH-DEBUG] Phase 3 summary

  for (const action of reorderActions) {
    try {
      await api.post(ENDPOINTS.REORDER_STEPS, {
        orchestration_id: targetOrchestrationId,
        new_order: action.value.new_order,
      });
      results.applied++;
    } catch (err) {
      results.failed.push({
        action,
        error: err.message || 'Failed to reorder steps',
      });
    }
  }

  // Phase 4: Remove steps LAST so that step_order references in earlier phases
  // stay valid for the whole batch. Error resilient.
  const removeActions = actions.filter((a) => a.type === 'remove_step');

  // [AI-ORCH-DEBUG] Phase 4 summary

  for (const action of removeActions) {
    try {
      await api.post(ENDPOINTS.DELETE_STEP_BY_ORDER, {
        orchestration_id: targetOrchestrationId,
        step_order: action.value.step_order,
      });
      results.applied++;
    } catch (err) {
      results.failed.push({
        action,
        error: err.message || 'Failed to remove step',
      });
    }
  }

  // [AI-ORCH-DEBUG] Final result

  return results;
}

/**
 * Apply a `create_orchestration` action against the existing manual-flow endpoint.
 *
 * This is a verbatim port of the multipart construction in
 * `orchestrationApi.createOrchestration`. We intentionally do not import that helper
 * because it dispatches Redux state, which is the modal's responsibility, not the
 * applier's.
 *
 * @param {Object} action - A validated `create_orchestration` action
 * @returns {Promise<{orchestration_id: number}>} The created orchestration data
 * @throws {Error} If the request fails or the response is not OK
 */
async function applyCreateOrchestration(action) {
  const { name, environment_id, dataset_id, project_id } = action.value;

  const formData = new FormData();

  // Backend expects payload wrapped in { payload: { ... } } with snake_case fields.
  // Include dataset_id only when an existing dataset is being bound (skip when null/undefined).
  // Include project_id so the orchestration is created under the correct project.
  const jsonPayload = {
    payload: {
      name,
      environment_id,
      ...(dataset_id ? { dataset_id } : {}),
      ...(project_id ? { project_id } : {}),
    },
  };

  const jsonBlob = new Blob([JSON.stringify(jsonPayload)], {
    type: 'application/json',
  });
  formData.append('payload', jsonBlob);

  // Backend's multipart contract requires a `dataFile` part. Send an empty CSV blob
  // since this flow does not upload a new dataset (matches the manual modal when
  // no file is picked).
  const emptyFile = new Blob([''], { type: 'text/csv' });
  formData.append('dataFile', emptyFile, 'empty.csv');

  const response = await fetch(ENDPOINTS.CREATE_ORCHESTRATION, {
    method: 'POST',
    body: formData,
    headers: {
      ...authHeader(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMessage = 'Failed to create orchestration';
    try {
      const errorData = await response.json();
      errorMessage =
        errorData?.error_description ||
        errorData?.message ||
        errorData?.error ||
        errorMessage;
    } catch {
      // Body was not JSON or already consumed — fall back to default message
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  if (!data?.data) {
    throw new Error('Invalid response from server - orchestration data missing');
  }
  return data.data;
}

/**
 * Fetch an API request's full detail by id and map it into the fields needed to
 * seed a new orchestration step (method, url, headers, query params, body).
 *
 * This mirrors the manual "Add Step" flow, which sends the API request's real
 * configuration so the new step is not created blank. The AI add-step action
 * only carries a `request_id`, so we resolve the rest here.
 *
 * Resilient by design: if the detail fetch fails or returns nothing, we return
 * a minimal payload (empty body/headers) so the add still succeeds.
 *
 * @param {number} requestId - The API request id to resolve
 * @returns {Promise<{method: string, url: string, request_header: Array, request_query_parameters: Array, request_body: {type: string, value: string}}>}
 */
async function buildAddStepPayloadFromApiRequest(requestId) {
  const fallback = {
    method: 'POST',
    url: '',
    authorization: { type: 'inherit', value: [] },
    request_header: [],
    request_query_parameters: [],
    request_body: { type: 'raw', value: '' },
    scripts: [],
  };

  try {
    const resp = await api.get(`/api/request/${requestId}`);
    const d = resp?.data?.data ?? resp?.data;
    if (!d) return fallback;

    // request_body can come back as a string or as a { value } object.
    let bodyValue = '';
    const rawBody = d.request_body;
    if (typeof rawBody === 'string') {
      bodyValue = rawBody;
    } else if (rawBody && typeof rawBody === 'object') {
      bodyValue =
        rawBody.value != null
          ? String(rawBody.value)
          : rawBody.body != null
            ? String(rawBody.body)
            : '';
    }

    const headers = (d.request_headers || [])
      .filter((h) => h && h.status !== 'D')
      .map((h) => ({
        key: h.key || h.name || '',
        value: h.value || '',
        headerType: h.headerType || h.header_type || 'custom',
        status: h.status || 'A',
        description: h.description || 'No description',
      }));

    const queryParams = (d.query_params || d.request_query_parameters || [])
      .filter((p) => p && p.status !== 'D')
      .map((p) => ({ key: p.key || p.name || '', value: p.value || '' }));

    // Authorization: the backend resolves the auth type by its CODE and expects
    // value as an array of { key, value }. The request-detail response already
    // carries the real DB code, so we pass it straight through.
    let authorization = { type: 'inherit', value: [] };
    const a = d.authorization;
    if (a && (a.code || a.name)) {
      const details = (a.details || a.value || []).map((det) => ({
        key: det.key || det.key_name || '',
        value: det.value || '',
      }));
      authorization = { type: a.code || a.name, value: details };
    }

    // Pre/post scripts: copied via the dedicated script endpoint after the add,
    // since addAPIToOrchestrationV2 does not persist scripts itself. Normalize
    // each script's source into the {"code":"..."} wrapper the backend stores.
    const scripts = (d.scripts || [])
      .map((s) => {
        let execTime = s.execution_time || s.executionTime || '';
        if (!execTime) {
          if (s.type === 'pre-request') execTime = 'prerequest';
          else if (s.type === 'post-response') execTime = 'postresponse';
        }
        if (execTime !== 'prerequest' && execTime !== 'postresponse') return null;
        let code = '';
        const raw = s.source_code ?? s.sourceCode ?? '';
        try {
          code = JSON.parse(raw).code ?? '';
        } catch {
          code = typeof raw === 'string' ? raw : '';
        }
        if (!code) return null;
        return {
          execution_time: execTime,
          type: 'text/javascript',
          source_code: JSON.stringify({ code }),
        };
      })
      .filter(Boolean);

    return {
      method: (d.method_name || 'POST').toUpperCase(),
      url: d.raw_url || '',
      authorization,
      request_header: headers,
      request_query_parameters: queryParams,
      request_body: { type: 'raw', value: bodyValue },
      scripts,
    };
  } catch (err) {
    console.warn('[AI-ORCH-DEBUG] applier.phase1.seedFailed', {
      requestId,
      error: err.message,
    });
    return fallback;
  }
}

/**
 * Apply a single modification action to the appropriate backend endpoint.
 *
 * @param {Object} action - A validated orchestration action
 * @param {number} orchestrationId - Current orchestration ID
 * @returns {Promise<*>} Backend response
 * @throws {Error} If the action type is unknown or the request fails
 */
async function applyModificationAction(action, orchestrationId) {
  switch (action.type) {
    case 'set_step_post_script':
    case 'set_step_pre_script':
      return api.post(ENDPOINTS.UPDATE_STEP_SCRIPT, {
        orchestration_id: orchestrationId,
        step_order: action.value.step_order,
        scripts: [
          {
            execution_time:
              action.type === 'set_step_post_script'
                ? 'postresponse'
                : 'prerequest',
            type: 'text/javascript',
            source_code: JSON.stringify({ code: action.value.script }),
          },
        ],
      });

    case 'set_step_header':
      return api.post(ENDPOINTS.UPDATE_STEP_HEADERS, {
        orchestration_id: orchestrationId,
        step_order: action.value.step_order,
        new_headers: (action.value.headers || []).map((h) => ({
          key: h.key || '',
          value: h.value || '',
        })),
      });

    case 'set_step_body': {
      const bodyType = (action.value.body_type || '').toLowerCase();
      const payload = {
        orchestration_id: orchestrationId,
        step_order: action.value.step_order,
        new_body: parseBodyValue(action.value.body),
      };
      // For structured formats, persist the mode + structured rows so the step
      // actually sends multipart / url-encoded (not just a raw string).
      if (bodyType === 'form-data' || bodyType === 'x-www-form-urlencoded') {
        payload.mode = bodyType;
        payload.mode_properties = toModeProperties(
          bodyType,
          typeof action.value.body === 'string' ? action.value.body : '',
        );
      } else if (bodyType === 'json' || bodyType === 'raw' || bodyType === 'text') {
        // Reset the mode to raw so a step previously set to form-data/url-encoded
        // doesn't keep sending structured data after being switched to JSON/raw.
        payload.mode = 'raw';
      } else if (bodyType === 'none') {
        payload.mode = 'none';
      }
      return api.post(ENDPOINTS.UPDATE_STEP_BODY, payload);
    }

    case 'set_step_auth':
      return api.post(ENDPOINTS.UPDATE_STEP_AUTHORIZATION, {
        orchestration_id: orchestrationId,
        step_order: action.value.step_order,
        authorization_type: action.value.auth.type || 'inherit',
        authorization_details: formatAuthDetails(action.value.auth),
      });

    case 'set_step_params':
      return api.post(ENDPOINTS.UPDATE_STEP_QUERY_PARAMS, {
        orchestration_id: orchestrationId,
        step_order: action.value.step_order,
        query_parameters: (action.value.params || []).map((p) => ({
          key: p.key || '',
          value: p.value || '',
        })),
      });

    case 'set_environment':
      return api.post(ENDPOINTS.UPDATE_DEFAULT_ENVIRONMENT, {
        orchestration_id: orchestrationId,
        environment_id: action.value.environment_id,
      });

    case 'set_dataset':
      return api.post(ENDPOINTS.UPDATE_DEFAULT_DATASET, {
        orchestration_id: orchestrationId,
        dataset_id: action.value.dataset_id,
      });

    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

/**
 * Parse the body string value into an object for the backend.
 * The backend expects `new_body` as a Map<String, Object>.
 *
 * @param {string} body - The body string from the AI action
 * @returns {Object} Parsed body object
 */
function parseBodyValue(body) {
  if (!body) return {};

  // Try parsing as JSON object
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed;
    }
  } catch {
    // Not valid JSON — wrap as raw value
  }

  // Return as raw body structure
  return { raw: body };
}

/**
 * Format authorization details from the AI action into the backend expected format.
 *
 * @param {Object} auth - Auth object from AI action { type, ...credentials }
 * @returns {Array<{key: string, value: string}>} Formatted auth details
 */
function formatAuthDetails(auth) {
  if (!auth) return [];

  // Extract all fields except 'type' as key-value pairs
  const details = [];
  for (const [key, value] of Object.entries(auth)) {
    if (key === 'type') continue;
    details.push({
      key,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    });
  }
  return details;
}

/**
 * Group actions by their step_order value.
 *
 * @param {Array} actions - Array of modification actions
 * @returns {Object} Actions grouped by step_order
 */
function groupByStepOrder(actions) {
  return actions.reduce((groups, action) => {
    const step = action.value.step_order;
    (groups[step] = groups[step] || []).push(action);
    return groups;
  }, {});
}

export {
  applyCreateOrchestration,
  buildAddStepPayloadFromApiRequest,
  applyModificationAction,
  groupByStepOrder,
  parseBodyValue,
  formatAuthDetails,
  ENDPOINTS,
};
